import { ASSIGNMENT_TAG, CONCEPTUAL_LABEL_TAG } from './utils/tags'
import {
    normalizeAddFilesToAssignmentResponse,
    normalizeCreateTextAssignmentFromZipResponse,
    normalizeFetchAssignmentFiltersResponse,
    normalizeSearchAssignmentsResponse,
} from './utils/normalizers/assignment.normalizers'
import { actions as assignmentActions } from '../reducers/assignment.reducer'
import { ASSIGNMENT_TRIAL_ACCESS_TYPES } from '../../constants/assignment-trial-access-types'
import baseApi from './base.api'
import { isNil } from 'lodash'
import { assignmentsPersistActions } from '../slices/assignments.persist.slice'
import getConfig from '../../configs/config'
import { createSearchParams } from '../../utils/request.helper'
import { ASSIGNMENT_SORTING_TYPES } from '../../constants/sorting-types'

export const assignmentApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        fetchAssignments: builder.query({
            query: ({ 
                name, 
                sort = ASSIGNMENT_SORTING_TYPES.NEW_TO_OLD.value, 
                type, 
                language, 
                page, 
                pageSize, 
                organizationIds, 
                courseIds, 
                searchType = 1,
                ownerIds,
                permissionLevel
            }) => {
                const params = createSearchParams({
                    sort_by: sort,
                    name,
                    page,
                    page_size: pageSize,
                    assignment_type: type,
                    language,
                    organization_used_ids: organizationIds,
                    course: courseIds,
                    search_type: searchType,
                    owner_ids: ownerIds,
                    permission_level: permissionLevel
                })

                return {
                    url: '/assignments/get-assignments',
                    method: 'GET',
                    params,
                }
            },
            providesTags: (result) => 
                result
                    ? [
                        { type: ASSIGNMENT_TAG, id: 'LIST' },
                        ...result.assignments.map(assignment => ({ type: ASSIGNMENT_TAG, id: assignment.token }))
                    ]
                    : [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createAssignmentFromZip: builder.mutation({
            query: ({ file, name, type, requireTrainset = false }) => {
                const formData = new FormData()
                formData.append('zip_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)
                if (!isNil(requireTrainset)) formData.append('require_trainset', requireTrainset)

                return {
                    url: '/assignments-upload/upload-zip-cs',
                    method: 'POST',
                    body: formData,
                    baseURL: getConfig().uploadsApiBaseUrl,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromCsv: builder.mutation({
            query: ({ file, name, type, submission_type }) => {
                const formData = new FormData()
                if (file) formData.append('file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)
                if (submission_type !== undefined) formData.append('submission_type', submission_type)

                return {
                    url: '/assignments-upload/upload-free-text',
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromCsvOld: builder.mutation({
            query: ({ file, name, type }) => {
                const formData = new FormData()
                formData.append('csv_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)

                return {
                    url: '/assignments-upload/upload-csv-cs-text',
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createTextAssignmentFromZip: builder.mutation({
            query: ({ file, name, type, submission_type }) => {
                const formData = new FormData()
                if (file) formData.append('zip_file', file)
                formData.append('assignment_name', name)
                formData.append('assignment_type', type)
                if (submission_type !== undefined) formData.append('submission_type', submission_type)

                return {
                    url: '/assignments-upload/upload-zip-cs-text',
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: normalizeCreateTextAssignmentFromZipResponse,
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createMba: builder.mutation({
            query: ({ versionId, name, targetUserId, trialAccess }) => ({
                url: `/assignments-upload/create-mba`,
                method: 'POST',
                body: {
                    version_id: versionId,
                    assignment_name: name,
                    target_user_id: targetUserId,
                    trial_access:
                        !trialAccess.type || ASSIGNMENT_TRIAL_ACCESS_TYPES.NONE.value === trialAccess.type
                            ? null
                            : {
                                  type: trialAccess.type,
                                  max_submissions: trialAccess.numberOfSubmissions,
                                  expiration_date: trialAccess.expirationDate,
                              },
                },
            }),
            invalidatesTags: (result, error, arg) =>
                arg.isCreatingForCurrentUser ? [{ type: ASSIGNMENT_TAG, id: 'LIST' }] : [],
        }),
        updateAssignmentSettings: builder.mutation({
            query: ({
                token,
                name,
                description,
                difficultyLevel,
                accessType,
                timeToSolveInMinutes,
                prerequisites,
                willLearn,
                sampleSubmissionIds,
                startDate,
                endDate,
                sendReasoningToStudents,
                showUnitTestTips,
                disciplines,
                dispenserOn,
                feedbackPhilosophy,
            }) => ({
                url: `/assignment/${token}`,
                method: 'PATCH',
                body: {
                    assignment_name: name,
                    description,
                    difficulty_level: difficultyLevel,
                    access_type: accessType,
                    time_to_solve: timeToSolveInMinutes,
                    prerequisites,
                    will_learn: willLearn,
                    sample_submissions: sampleSubmissionIds,
                    start_date: startDate,
                    end_date: endDate,
                    send_explainability_feedback: sendReasoningToStudents,
                    llm_ut_explanation: showUnitTestTips,
                    disciplines,
                    dispenser_on: dispenserOn,
                    philosophy_text: feedbackPhilosophy,
                },
            }),
            invalidatesTags: [{ type: CONCEPTUAL_LABEL_TAG, id: 'LIST' }],
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
        }),
        addFilesToAssignment: builder.mutation({
            query: ({ token, file }) => {
                const formData = new FormData()
                formData.append('file', file)

                return {
                    url: `/assignments-upload/${token}/add-files`,
                    method: 'POST',
                    body: formData,
                }
            },
            transformResponse: normalizeAddFilesToAssignmentResponse,
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
        }),
        fetchAssignmentMbas: builder.query({
            query: ({ token }) => ({
                url: `/assignment/${token}/mbas`,
                method: 'GET',
            }),
        }),
        cloneAssignment: builder.mutation({
            query: ({ token, name, userId, includeMbas = false }) => ({
                url: `/assignments-upload/${token}/copy`,
                method: 'POST',
                body: { assignment_name: name, user_id: userId, forward_mbas: includeMbas },
            }),
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        createAlertReview: builder.mutation({
            query: ({ token, email, resolution }) => ({
                url: `/assignment-metadata/${token}/update-alert-suppression`,
                method: 'PUT',
                body: { reviewer: email, review_resolution: resolution },
            }),
        }),
        fetchModels: builder.query({
            query: () => ({
                url: `/assignments/get-models`,
                method: 'GET',
            }),
        }),
        searchAssignments: builder.query({
            query: ({ name }) => ({
                url: `/assignments/search`,
                method: 'GET',
                params: { name, limit: 5 },
            }),
            transformResponse: normalizeSearchAssignmentsResponse,
        }),
        fetchAssignmentFilters: builder.query({
            query: ({searchType}) => ({
                url: `/assignments/filters`,
                method: 'GET',
                params: searchType ? { search_type: searchType } : undefined,
            }),
            providesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
            transformResponse: normalizeFetchAssignmentFiltersResponse,
        }),
        trainAndProjectTextModel: builder.mutation({
            query: ({ token }) => ({
                url: `/text-models/train-and-project`,
                method: 'POST',
                params: { assignment_token: token },
            }),
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => dispatch(assignmentActions.fetchAssignmentInfo(arg.token)))
            },
            invalidatesTags: (result, error, arg) => [{ type: ASSIGNMENT_TAG, id: arg.token }],
        }),
        findInconsistenciesInTextModel: builder.mutation({
            query: ({ token }) => ({
                url: `/text-models/find-inconsistencies`,
                method: 'POST',
                params: { assignment_token: token },
            }),
            onQueryStarted: (arg, { dispatch, queryFulfilled }) => {
                queryFulfilled.then(() => {
                    dispatch(assignmentActions.fetchAssignmentInfo(arg.token))
                    dispatch(assignmentsPersistActions.setConsistencyCheckCompleted(true))
                })
            },
        }),
        exportModel: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-assignment`,
                baseURL: getConfig().uploadsApiBaseUrl,
                method: 'GET',
                body: {
                    response_format: 'zip',
                },
                responseType: 'blob',
            }),
        }),
        exportMatches: builder.query({
            query: ({ token }) => ({
                url: `/assignments-upload/${token}/export-pattern-matches`,
                method: 'GET',
                body: {
                    response_format: 'csv',
                },
                responseType: 'blob',
            }),
        }),
        importModel: builder.mutation({
            query: ({ file }) => {
                const formData = new FormData()
                formData.append('file', file)

                return {
                    url: `/assignments-upload/import-assignment`,
                    baseURL: getConfig().uploadsApiBaseUrl,
                    method: 'POST',
                    body: formData,
                }
            },
            invalidatesTags: [{ type: ASSIGNMENT_TAG, id: 'LIST' }],
        }),
        downloadNewTextSubmissions: builder.query({
            query: ({ token, exportMode }) => ({
                url: `/assignment/${token}/download-submissions`,
                method: 'GET',
                body: {
                    response_format: 'csv',
                },
                params: {
                    export_mode: exportMode,
                },
                responseType: 'blob',
            }),
        }),
        exportWorksheet: builder.query({
            query: ({ token, docFormat }) => ({
                url: `/questions/${token}/export-template`,
                method: 'GET',
                params: {
                    doc_format: docFormat,
                },
                responseType: 'blob',
            }),
        }),
    }),
})

export const {
    useFetchAssignmentsQuery,
    useCreateAssignmentFromZipMutation,
    useCreateTextAssignmentFromCsvMutation,
    useCreateTextAssignmentFromCsvOldMutation,
    useCreateTextAssignmentFromZipMutation,
    useCreateMbaMutation,
    useUpdateAssignmentSettingsMutation,
    useAddFilesToAssignmentMutation,
    useFetchAssignmentMbasQuery,
    useCloneAssignmentMutation,
    useCreateAlertReviewMutation,
    useFetchModelsQuery,
    useSearchAssignmentsQuery,
    useFetchAssignmentFiltersQuery,
    useTrainAndProjectTextModelMutation,
    useFindInconsistenciesInTextModelMutation,
    useLazyExportModelQuery,
    useLazyExportMatchesQuery,
    useLazyExportWorksheetQuery,
    useImportModelMutation,
    useLazyDownloadNewTextSubmissionsQuery,
} = assignmentApi
